import { translateCaseDocumentType, translateFinancialAccountDocumentType } from '@common/intl'
import type { CaseEventFragment, UserFragment } from '@gql'
import { CaseEventType, Permission } from '@gql'
import { Tooltip } from '@mui/material'
import { hasPermissions } from '@sections/auth/access'
import { formatReminderTime } from '@sections/cases/utils/reminders'
import { userDetailUrl } from '@sections/users/urls'
import { formatAddress } from '@utils/formatAddress'
import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'
import { Link } from 'react-router'

import { caseIdentityMessages } from './case-identity'

/** A customer named by a move, merge or identity event; it may be gone since. */
const customerLabel = (
  intl: IntlShape,
  customer: CaseEventFragment['toCustomer'],
): string | ReactNode =>
  customer ? (
    <b>
      {customer.name} ({customer.code})
    </b>
  ) : (
    intl.formatMessage(caseIdentityMessages.eventUnknownCustomer)
  )

export function translateCaseEvent(
  intl: IntlShape,
  event: CaseEventFragment,
  user: UserFragment | null | undefined,
): string | ReactNode {
  switch (event.type) {
    case CaseEventType.CLOSED:
      return intl.formatMessage({
        defaultMessage: 'Case was closed',
        description: 'case history message',
      })
    case CaseEventType.ASSIGNED:
      return intl.formatMessage(
        {
          defaultMessage: 'Case was assigned to user {userName}',
          description: 'case history message',
        },
        {
          userName: event.user ? (
            hasPermissions([Permission.MANAGE_USERS], user) ? (
              <Link key={event.user.id} to={userDetailUrl(event.user.id)}>
                <Tooltip disableInteractive title={event.user.email}>
                  <b>{event.user.displayName}</b>
                </Tooltip>
              </Link>
            ) : (
              <b>{event.user.displayName}</b>
            )
          ) : (
            'unknown'
          ),
        },
      )
    case CaseEventType.DOCUMENT_UPLOADED: {
      if (event.financialAccount) {
        return intl.formatMessage(
          {
            defaultMessage:
              'Document {type} was uploaded for financial account {financialAccountName}.',
            description: 'case history message',
          },
          {
            financialAccountName: event.financialAccount.name,
            type: (
              <b>
                {translateFinancialAccountDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage(
          {
            defaultMessage: 'Document {type} was uploaded.',
            description: 'case history message',
          },
          {
            type: (
              <b>
                {translateCaseDocumentType(intl, event.parameters.type, event.parameters.otherType)}
              </b>
            ),
          },
        )
      }
    }

    case CaseEventType.DOCUMENT_EDITED: {
      if (event.financialAccount) {
        return intl.formatMessage(
          {
            defaultMessage:
              'Document {type} for financial account {financialAccountName} was edited.',
            description: 'case history message',
          },
          {
            financialAccountName: event.financialAccount.name,
            type: (
              <b>
                {translateFinancialAccountDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage(
          {
            defaultMessage: 'Document {type} was edited.',
            description: 'case history message',
          },
          {
            type: (
              <b>
                {translateCaseDocumentType(intl, event.parameters.type, event.parameters.otherType)}
              </b>
            ),
          },
        )
      }
    }

    case CaseEventType.DOCUMENT_DELETED: {
      if (event.financialAccount) {
        return intl.formatMessage(
          {
            defaultMessage:
              'Document {type} for financial account {financialAccountName} was deleted.',
            description: 'case history message',
          },
          {
            financialAccountName: event.financialAccount.name,
            type: (
              <b>
                {translateFinancialAccountDocumentType(
                  intl,
                  event.parameters.type,
                  event.parameters.otherType,
                )}
              </b>
            ),
          },
        )
      } else {
        return intl.formatMessage(
          {
            defaultMessage: 'Document {type} was deleted.',
            description: 'case history message',
          },
          {
            type: (
              <b>
                {translateCaseDocumentType(intl, event.parameters.type, event.parameters.otherType)}
              </b>
            ),
          },
        )
      }
    }

    case CaseEventType.CREATED:
      return intl.formatMessage({
        defaultMessage: 'Case was created',
        description: 'case history message',
      })
    case CaseEventType.NOTE_ADDED:
      return intl.formatMessage({
        defaultMessage: 'Note added',
        description: 'case history message',
      })
    case CaseEventType.FINANCIAL_ACCOUNT_ADDED:
      return intl.formatMessage(
        {
          defaultMessage: 'Financial account {financialAccountName} created',
          description: 'case history message',
        },
        {
          financialAccountName: event.parameters.financialAccountName,
        },
      )
    case CaseEventType.FINANCIAL_ACCOUNT_EDITED:
      return intl.formatMessage(
        {
          defaultMessage: 'Financial account {financialAccountName} edited',
          description: 'case history message',
        },
        {
          financialAccountName: event.parameters.financialAccountName,
        },
      )
    case CaseEventType.FINANCIAL_ACCOUNT_DELETED:
      return intl.formatMessage(
        {
          defaultMessage: 'Financial account {financialAccountName} has been deleted',
          description: 'case history message',
        },
        {
          financialAccountName: event.parameters.financialAccountName,
        },
      )
    case CaseEventType.ACTION_ADDED:
      return intl.formatMessage(
        {
          defaultMessage: '{type} action with result {result} created',
          description: 'case history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )
    case CaseEventType.ACTION_EDITED:
      return intl.formatMessage(
        {
          defaultMessage: '{type} action with result {result} edited',
          description: 'case history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )
    case CaseEventType.ACTION_DELETED:
      return intl.formatMessage(
        {
          defaultMessage: '{type} action with result {result} has been deleted',
          description: 'case history message',
        },
        {
          result: event.actionResult?.name,
          type: event.actionType?.name,
        },
      )

    case CaseEventType.ADDRESS_ASSIGNED:
      return intl.formatMessage(
        {
          defaultMessage: 'Address {address} has been set for this case.',
          description: 'case history message',
        },
        {
          address: formatAddress(event.parameters),
        },
      )
    case CaseEventType.EMAIL_ASSIGNED:
      return intl.formatMessage(
        {
          defaultMessage:
            'Email {emailAddress} has been set for this case<isDefault> and set to default</isDefault>.',
          description: 'case history message',
        },
        {
          emailAddress: event.parameters.emailAddress,
          isDefault: (...chunks: any[]) => (event.parameters.isDefault ? chunks : null),
        },
      )
    case CaseEventType.PHONE_ASSIGNED:
      return intl.formatMessage(
        {
          defaultMessage:
            'Phone {phoneNumber} has been set for this case<isDefault> and set to default</isDefault>.',
          description: 'case history message',
        },
        {
          isDefault: (...chunks: any[]) => (event.parameters.isDefault ? chunks : null),
          phoneNumber: event.parameters.phoneNumber,
        },
      )
    case CaseEventType.REOPENED:
      return intl.formatMessage({
        defaultMessage: 'Case was reopened.',
        description: 'case history message',
      })

    case CaseEventType.TAG_ATTACHED:
      if (event.parameters.tagName) {
        return intl.formatMessage(
          {
            defaultMessage: 'Tag {tagName} attached.',
            description: 'case history message',
          },
          {
            tagName: event.parameters.tagName,
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Tag attached.',
          description: 'case history message',
        })
      }

    case CaseEventType.TAG_DETACHED:
      if (event.parameters.tagName) {
        return intl.formatMessage(
          {
            defaultMessage: 'Tag {tagName} detached.',
            description: 'case history message',
          },
          {
            tagName: event.parameters.tagName,
          },
        )
      } else {
        return intl.formatMessage({
          defaultMessage: 'Tag detached.',
          description: 'case history message',
        })
      }

    // Reminder events carry no note (it is personal data); the time is
    // shown in Jakarta, like everywhere else reminders appear.
    case CaseEventType.REMINDER_ADDED:
      return intl.formatMessage(
        {
          defaultMessage:
            '{isImport, select, true {The customer booked a callback for {dueAt}.} other {Reminder set for {dueAt}.}}',
          description: 'case history message',
        },
        {
          dueAt: <b>{formatReminderTime(String(event.parameters?.dueAt ?? ''), intl.locale)}</b>,
          isImport: String(event.parameters?.source === 'IMPORT'),
        },
      )
    case CaseEventType.REMINDER_RESCHEDULED:
      return intl.formatMessage(
        {
          defaultMessage:
            '{isImport, select, true {The customer moved the callback to {dueAt}.} other {Reminder moved to {dueAt}.}}',
          description: 'case history message',
        },
        {
          dueAt: <b>{formatReminderTime(String(event.parameters?.dueAt ?? ''), intl.locale)}</b>,
          isImport: String(event.parameters?.source === 'IMPORT'),
        },
      )
    case CaseEventType.REMINDER_DONE:
      return intl.formatMessage({
        defaultMessage: 'Reminder done.',
        description: 'case history message',
      })
    case CaseEventType.REMINDER_CANCELLED:
      return intl.formatMessage(
        {
          defaultMessage:
            '{reason, select, CASE_CLOSED {Reminder cancelled because the case was closed.} ERASED {Reminder cancelled because the customer data was erased.} other {Reminder cancelled.}}',
          description: 'case history message',
        },
        { reason: String(event.parameters?.reason ?? 'OPERATOR') },
      )

    // The note an operator adds rides as its own NOTE_ADDED event.
    case CaseEventType.IDENTITY_CONFIRMED:
      return intl.formatMessage(caseIdentityMessages.eventConfirmed, {
        customer: customerLabel(intl, event.toCustomer),
      })
    case CaseEventType.MOVED_TO_CUSTOMER:
      return intl.formatMessage(caseIdentityMessages.eventMoved, {
        from: customerLabel(intl, event.fromCustomer),
        to: customerLabel(intl, event.toCustomer),
      })
    case CaseEventType.CUSTOMER_MERGED:
      // A merge hard-deletes its source, so `fromCustomer` is normally gone.
      return event.fromCustomer
        ? intl.formatMessage(caseIdentityMessages.eventCustomerMerged, {
            from: customerLabel(intl, event.fromCustomer),
            to: customerLabel(intl, event.toCustomer),
          })
        : intl.formatMessage(caseIdentityMessages.eventCustomerMergedNoSource, {
            to: customerLabel(intl, event.toCustomer),
          })

    default:
      // A type this build doesn't know yet — show something rather than an
      // empty history row.
      return intl.formatMessage(
        {
          defaultMessage: 'Case was updated ({type}).',
          description: 'case history message',
        },
        { type: String(event.type).toLowerCase().replaceAll('_', ' ') },
      )
  }
}
